/**
 * Every string the interface says, in English.
 *
 * Flat dotted keys rather than nested objects: the lookup is a single property
 * read, a missing key is obvious in a diff, and the two files line up row for
 * row when you compare them.
 *
 * Preset names and descriptions are the exception to "the dictionary owns the
 * copy" — they live in `@studio/shared` because the prompts are built from the
 * same objects. The keys below override them for display only, so the prompt
 * that reaches the renderer stays English.
 */
export const en = {
  /* ------------------------------- shell ------------------------------- */
  'app.skip': 'Skip to content',
  'app.sections': 'Studio sections',
  'hub.eyebrow': 'New shoot',
  'hub.title': 'What are we shooting today?',
  'hub.lede': 'Three ways to set up a shoot. Each asks only for what it needs.',
  'hub.recent': 'Recent shoots',
  'hub.live': 'Live',
  'hub.copy.title': 'Recreate a scene',
  'hub.copy.text':
    'Pick one of the Studio’s scenes: your model and your clothes take the place of hers, and everything else stays the same.',
  'hub.copy.go': 'Pick a scene',
  'hub.presets.title': 'Model, places and poses',
  'hub.presets.text':
    'A Studio model or your own, your clothes, places and poses as photos: each pose is reproduced exactly.',
  'hub.presets.go': 'Compose',
  'hub.brief.title': 'Describe the shoot',
  'hub.brief.text':
    'With the stylist: three outfits, three places and three poses to pick from, then you adjust version after version, as you would on set.',
  'hub.brief.go': 'Talk to the stylist',
  'stylist.title': 'The stylist',
  'stylist.name': 'The stylist',
  'stylist.fiche': 'What is decided',
  'stylist.toChoose': 'to choose',
  'stylist.step.model': 'Model',
  'stylist.step.pieces': 'Pieces',
  'stylist.say.model': 'Say who, or tap a portrait…',
  'stylist.say.pieces': 'Or name the pieces…',
  'stylist.say.choose': '“The 2”, or your own idea…',
  'stylist.say.review': 'What to change…',
  'stylist.typing': 'The stylist is writing',
  'stylist.studioModel': 'Studio',
  'stylist.ownModel': 'Your models',
  'stylist.createModel': 'Create a model',
  'stylist.addPiece': 'Add a piece',
  'stylist.change': 'change',
  'stylist.surprise': 'Surprise me',
  'stylist.develop': 'Film & grain',
  'stylist.new': 'New conversation',
  'stylist.openRoll': 'See the roll',
  'stylist.resume': 'Pick up a conversation',
  'stylist.untitled': 'Untitled',
  'stylist.loadFailed': 'The conversation could not be opened.',
  'stylist.failed': 'The stylist could not answer. Try again.',
  'stylist.ask.model': 'Hello! Which model would you like for this shoot?',
  'stylist.ask.pieces': 'Lovely. Which pieces do you want to shoot?',
  'stylist.piecesDone': 'That’s all — show me outfits',
  'stylist.options.outfit': 'Three ways to wear them. Which do you like?',
  'stylist.options.place': 'For this outfit, three places:',
  'stylist.options.pose': 'And three poses for this place:',
  'stylist.step.outfit': 'Outfit',
  'stylist.step.place': 'Place',
  'stylist.step.pose': 'Pose',
  'stylist.other': 'Something else',
  'stylist.otherHint.outfit': 'Describe the outfit you want',
  'stylist.otherHint.place': 'Describe the place you want',
  'stylist.otherHint.pose': 'Describe the pose you want',
  'stylist.otherGo': 'Choose this',
  'stylist.more': 'Other ideas',
  'stylist.makesVersion': 'Choosing shoots a version · {cost}',
  'stylist.versionN': 'Version {n}',
  'stylist.correction': 'Correction',
  'stylist.shooting': 'Shooting…',
  'stylist.correcting': 'Correcting…',
  'stylist.wait': 'Let me finish this version first, then tell me what to change.',
  'stylist.unclear':
    'I could not tell what to change. Tell me the outfit, the place or the pose — by number or in your words — or what to fix on the photograph.',
  'stylist.finishes': 'Camera settings',
  'stylist.costEach': '{cost} a version',
  'stylist.send': 'Send',
  'stylist.idea.pose': 'I don’t like the pose, use pose 2',
  'stylist.idea.place': 'Keep the outfit, with place 3',
  'stylist.idea.blur': 'More blur behind her',
  'stylist.idea.more': 'Show me other poses',
  'source.failed': 'Could not import that image.',
  'source.notAnImage': 'That file is not a PNG, JPEG or WebP image.',
  'source.remove': 'Remove the image',
  'source.working': 'Importing…',
  'source.dropOrClick': 'Drop an image here or click to choose one',
  'source.urlPlaceholder': '… or paste the address of an image or a page',
  'source.urlLabel': 'Address of an image or a web page',
  'source.import': 'Import',
  'scenewiz.step.model': 'Model',
  'scenewiz.step.clothes': 'Clothes',
  'scenewiz.step.scenes': 'Scenes',
  'scenewiz.step.outfit': 'Outfit',
  'scenewiz.step.finish': 'Finishing',
  'scenewiz.step.go': 'Go',
  'scenewiz.title.model': 'Who wears your clothes?',
  'scenewiz.title.clothes': 'What is she wearing?',
  'scenewiz.title.scenes': 'In which scenes?',
  'scenewiz.title.outfit': 'How to wear them?',
  'scenewiz.title.finish': 'A few finishing touches',
  'scenewiz.title.go': 'Ready to shoot',
  'scenewiz.lede.model': 'A Studio model, or your own.',
  'scenewiz.lede.clothes': 'Add photos of your pieces, or take them from your wardrobe.',
  'scenewiz.lede.scenes':
    'The Studio’s scenes: your model and your clothes take the place of hers, and the place, light, pose and framing stay the same. One photo per scene.',
  'scenewiz.lede.outfit':
    'For each scene, the stylist proposes three looks with your pieces. Pick one, or describe your own.',
  'scenewiz.lede.finish': 'Optional — the defaults are fine.',
  'scenewiz.lede.go': 'Check, name it if you like, and go.',
  'scenewiz.need.scenes': 'Choose at least one scene.',
  'scenewiz.need.outfit': 'Choose a look for each scene.',
  'scenewiz.looksFailed': 'The stylist could not propose looks for this scene.',
  'scenewiz.looksAgain': 'Try again',
  'scenewiz.otherHint': 'E.g. the jacket closed, tucked into the skirt, no bag',
  'scenewiz.n.scene': '{n} scene',
  'scenewiz.n.scenes': '{n} scenes',
  'scenewiz.emptyStaff':
    'No scenes yet. Add some in House → Catalogue → Scenes, or from the loupe of one of your photos.',
  'scenewiz.qualityHint':
    'Each scene is edited in one message (the scene, your model, your clothes): about {cost} a photo.',
  'copy.eyebrow': 'Recreate a scene',
  'copy.go': 'Recreate the scene',
  'copy.noScenes': 'The Studio has not offered any scenes yet.',
  'copy.piecesHint':
    'Each piece replaces the one worn the same way in the scene; what none replaces stays as it is.',
  'brief.revised': 'New version started: {name}.',
  'brief.reviseFailed': 'The new version could not be started.',
  'roll.recreated': 'Recreated photo',
  'roll.recreatedKept': 'Same place as the original photo.',
  'roll.recreatedMoved': 'Somewhere other than the original photo.',
  'roll.brief': 'The brief',
  'roll.previousVersion': '← Previous version',
  'roll.reviseLabel': 'Not quite it?',
  'roll.revisePlaceholder': 'E.g. more movement, make her smile, a tighter frame…',
  'roll.revise': 'Start a new version',
  'roll.revising': 'The studio is replanning…',
  'roll.reviseWait': 'Wait for the render to finish before asking for a new version.',
  'group.fashion': 'Fashion',
  'group.commercial': 'Commercial',
  'scene.customPlaceholder': 'A rain-slicked Tokyo side street at night, lit by vending machines…',
  'scene.photoHint':
    'The photo is matched for light, depth and perspective. Anyone already in it is excluded — only the setting is taken.',
  'error.no_models': 'Create a model first: a shoot needs someone to photograph.',
  'app.nav.shoot': 'Shoot',
  'app.nav.talent': 'Talent',
  'app.nav.rolls': 'Rolls',
  'app.nav.sheet': 'Sheet',
  'app.nav.account': 'Account',
  'app.nav.admin': 'Front desk',
  'app.signal': 'Services',
  'app.frames': 'Frames this month',
  'app.group.main': 'Main',
  'app.group.archive': 'Archive',
  'app.group.account': 'Account',
  'app.menu': 'Account and settings',
  'app.railOpen': 'Open the menu',
  'app.railClose': 'Close the menu',
  'search.title': 'Search the studio',
  'search.placeholder': 'Search faces, rolls and sections…',
  'search.none': 'Nothing here answers to “{query}”.',
  'search.group.sections': 'Go to',
  'search.group.talent': 'Faces',
  'search.group.rolls': 'Rolls',
  'app.signal.styling': 'Styling',
  'app.signal.analysis': 'Analysis',
  'app.signal.render': 'Render',
  'app.signal.live': 'live',
  'app.signal.preview': 'preview',
  'app.signal.checking': 'checking…',
  'app.theme': 'Appearance',
  'app.theme.dark': 'Dark',
  'app.theme.light': 'Light',
  'app.theme.system': 'System',
  'app.language': 'Language',

  /* ------------------------------ common ------------------------------- */
  'common.close': 'Close',
  'common.save': 'Save',
  'common.delete': 'Delete',
  'common.cancel': 'Cancel',
  'wiz.title': 'New shoot',
  'wiz.step.model': 'Model',
  'wiz.step.clothes': 'Clothes',
  'wiz.step.places': 'Places',
  'wiz.step.poses': 'Poses',
  'wiz.step.finish': 'Finishing',
  'wiz.step.go': 'Launch',
  'wiz.title.model': 'Who wears it?',
  'wiz.title.clothes': 'What does she wear?',
  'wiz.title.places': 'Where?',
  'wiz.title.poses': 'In which poses?',
  'wiz.title.finish': 'A few finishing touches',
  'wiz.title.go': 'Ready to shoot',
  'wiz.lede.model': 'A model from the Studio, or one of your own.',
  'wiz.lede.clothes': 'Add photos of your pieces, or take them from your wardrobe.',
  'wiz.lede.places': 'Choose one or more: each place multiplies the images.',
  'wiz.lede.poses':
    'Each pose is reproduced exactly, with your model’s face, your clothes and the place you choose next.',
  'wiz.lede.finish': 'Optional — the defaults work well.',
  'wiz.lede.go': 'Check, name it if you like, and launch.',
  'wiz.need.model': 'Choose a model.',
  'wiz.need.clothes': 'Add at least one piece.',
  'wiz.need.places': 'Choose at least one place.',
  'wiz.need.poses': 'Choose at least one pose.',
  'wiz.quality': 'Photo quality',
  'wiz.quality.plain': 'Standard',
  'wiz.quality.checked': 'Checked',
  'wiz.qualityHint.plain':
    'The most faithful renderer, in three steps (face, place, clothes), no automatic check: about {cost} a photo. Reshoot the ones you do not like yourself.',
  'wiz.engine.max': 'Maximum fidelity',
  'wiz.engine.standard': 'Economy',
  'wiz.engineHint.max': 'The most faithful renderer (Gemini Pro), at 2K.',
  'wiz.engineHint.standard':
    'The newer Flash renderer (Nano Banana 2), at 1K: about half the price — try it on one roll before you adopt it.',
  'wiz.qualityHint.checked':
    'In each place, the first pose is the reference photo, made in one message (pose, model, clothes, place). Approve it: the other poses are drawn from it in the same conversation, without touching the model, the clothes or the place. One image a photo: about {cost}.',
  'wiz.n.pose': '{n} pose',
  'wiz.n.poses': '{n} poses',
  'wiz.n.place': '{n} place',
  'wiz.n.places': '{n} places',
  'wiz.back': 'Back',
  'wiz.next': 'Next',
  'wiz.edit': 'Change',
  'wiz.name': 'Name of the shoot',
  'wiz.advanced': 'Advanced composer (camera angles, detailed settings)',
  'wiz.shelf.mine': 'Yours',
  'wiz.shelf.models': 'Models',
  'wiz.shelf.places': 'Places',
  'wiz.shelf.poses': 'Poses',
  'wiz.emptyStudioModelsStaff': 'No models offered yet. Add some from Admin → Catalogue.',
  'wiz.emptyStudioPosesStaff': 'No poses offered yet. Add some from Admin → Catalogue.',
  'wiz.kind.all': 'All',
  'wiz.kind.street': 'Streets',
  'wiz.kind.studio': 'Plain backgrounds',
  'wiz.studio': 'Studio',
  'wiz.location': 'Location',
  'wiz.studioSet': 'Studio set',
  'wiz.addModel': 'Create your model',
  'wiz.addPlace': 'Add a place',
  'wiz.addPose': 'Add poses',
  'wiz.addPoseHint': 'One pose per photo',
  'wiz.poseAdding': 'Adding {n}…',
  'wiz.poseFailed': 'That photo could not be added as a pose.',
  'wiz.placeFailed': 'That place could not be saved.',
  'wiz.placePhoto': 'A photo of the place (optional)',
  'wiz.placeName': 'Name',
  'wiz.placeNamePlaceholder': 'e.g. Our showroom',
  'wiz.placeDesc': 'Description',
  'wiz.placeDescPlaceholder': 'e.g. a white loft with tall windows and a concrete floor',
  'wiz.placeSave': 'Save the place',
  'wiz.removePlace': 'Delete {name}',
  'wiz.removePose': 'Delete {name}',
  'wiz.removeBody': 'It is removed from the library for good.',
  'wiz.removeGo': 'Delete',
  'wiz.rename': 'Rename',
  'wiz.renameNamed': 'Rename {name}',
  'wiz.renameFailed': 'That name could not be saved.',
  'wiz.emptyStudioModels': 'No models offered yet. Create your own under “Yours”.',
  'wiz.emptyMyPlaces': 'No places of your own yet.',
  'wiz.emptyStudioPoses': 'No poses offered yet. Add a photo of a pose under “Yours”.',
  'wiz.emptyMyPoses': 'No poses of your own yet: add a photo of someone in the pose you want.',
  'wiz.light': 'Light',
  'wiz.lightFrom.place': 'The place’s',
  'wiz.lightFrom.pose': 'The pose photo’s',
  'wiz.lightFrom.chosen': 'Choose one',
  'wiz.asInPose': 'As in the pose',
  'wiz.severalExpr': 'Each expression chosen is shot on every image: the shoot multiplies.',
  'sheet.previous': 'Previous frame',
  'sheet.next': 'Next frame',
  'sheet.discardTitle': 'Discard this model?',
  'wardrobe.confirmDeleteTitle': 'Delete {label}?',
  'shoot.recipeNameLabel': 'Name',
  'common.ok': 'OK',
  'common.deleteIt': 'Delete',
  'common.discardIt': 'Discard',
  'common.back': 'All rolls',
  'common.download': 'Download frame',
  'common.frames': 'frames',
  'common.frame': 'frame',
  'common.keeper': 'keeper',
  'common.keepers': 'keepers',
  'common.setUp': 'set-up',
  'common.setUps': 'set-ups',
  'sheet.discardConfirm': 'Discard this model? Everything you have entered will be lost.',

  /* ----------------------------- new shoot ----------------------------- */
  'shoot.eyebrow': 'Call sheet',
  'shoot.group.subject': 'The subject',
  'shoot.group.frames': 'The frames',
  'shoot.group.look': 'The look',
  'shoot.who': 'Who wears it',
  'shoot.what': 'What they wear',
  'shoot.styling': 'Styling',
  'enum.styling.as-uploaded': 'Only what is here',
  'enum.styling.complete': 'Complete the look',
  'shoot.stylingUploadedHint': 'The stylist dresses the model in the pieces above and nothing else.',
  'shoot.stylingCompleteHint':
    'Give one piece and the stylist adds what it lacks — a bottom, shoes, a layer, accessories — chosen to suit it and the place. Everything added is listed on the roll.',
  'shoot.accessories': 'Accessories',
  'shoot.accessoriesOther': 'Other accessories',
  'shoot.accessoriesPlaceholder': 'a silver signet ring, tortoiseshell sunglasses…',
  'shoot.accessoriesHint':
    'Added in words, with no photograph to match — say what matters, such as the metal or the colour.',
  'shoot.expression': 'Expression',
  'shoot.expressionHint': 'Set apart from the pose, so a catalogue pose can be shot smiling.',
  'enum.expression.auto': 'As the pose',
  'enum.expression.neutral': 'No smile',
  'enum.expression.hint': 'A hint',
  'enum.expression.soft': 'Soft smile',
  'enum.expression.open': 'Smile',
  'enum.expression.laugh': 'Laugh',
  'enum.expression.talking': 'Mid-sentence',
  'scene.auto': 'Let the stylist choose',
  'scene.autoDesc': 'Picked to suit the look, and written on the roll.',
  'scene.stylist': 'stylist',
  'scene.paris': 'Paris',
  'group.lifestyle': 'Lifestyle',
  'roll.stylistAdded': 'Added in words, without a photograph: {items}.',
  'shoot.attitude': 'Attitude',
  'enum.attitude.editorial': 'Editorial',
  'enum.attitude.influencer': 'Influencer',
  'enum.attitude.relaxed': 'Relaxed',
  'shoot.attitudeHint.editorial': 'Composed and chic, the way a lookbook carries itself.',
  'shoot.attitudeHint.influencer':
    'Candid and a little playful, as if a friend took it — and an outfit of the day rather than an editorial.',
  'shoot.attitudeHint.relaxed': 'Easy and unpretentious, and dressed down: casual rather than chic.',
  'shoot.fidelity': 'Fidelity to the pieces',
  'enum.fidelity.standard': 'Standard',
  'enum.fidelity.max': 'Maximum',
  'shoot.fidelityStandardHint': 'The quick renderer. Good for most rolls.',
  'shoot.fidelityMaxHint':
    'The stronger renderer at twice the resolution, with the description kept word for word in every frame. Holds lengths, button counts and embroidery far better — slower, and a few times the cost per frame.',
  'wardrobe.dropHere': 'Drop the photos here',
  'wardrobe.orDrag': 'or drag them here',
  'wardrobe.notAnImage': 'Only PNG, JPEG or WebP photos can be added.',
  'wardrobe.detailHint':
    'This piece has fine detail — add a close photo of it (“+ Detail” below) so the render can copy it.',
  'group.influencer': 'Influencer',
  'group.editorial': 'Editorial',
  'shoot.optional': 'optional',
  'shoot.setUpsN': '{n} set-ups',
  'shoot.setUpN': '{n} set-up',
  'shoot.takesPerSetUp': 'Takes of each angle',
  'shoot.severalHint': 'Each attitude chosen is shot on every frame: the roll multiplies.',
  'plan.n.models': '{n} models',
  'plan.n.angle': '{n} shot',
  'plan.n.angles': '{n} shots',
  'plan.n.positions': '{n} positions',
  'plan.n.scenes': '{n} scenes',
  'plan.n.attitudes': '{n} attitudes',
  'plan.n.expressions': '{n} expressions',
  'shoot.positionsDefault': 'Each angle keeps its own',
  'shoot.createModel': 'Create a model',
  'shoot.showTechnical': 'Show technical settings',
  'shoot.hideTechnical': 'Hide technical settings',
  'shoot.longRun': 'A long roll — this will take a while.',
  'shoot.notesHint':
    'Name anything you have no photo of, and say how the pieces layer. Plain sentences work best — this is read as instructions.',
  'plan.empty': 'Cast someone and choose an angle — the roll appears here.',
  'plan.andMore': '+ {n} more frames',
  'plan.someone': 'Someone',
  'shoot.step.positions': 'Positions',
  'shoot.wardrobeHint':
    'A flat lay, a hanger shot or a product cutout all work. One garment per photo; layer order sets the numbering the prompt refers to — IMG 01, IMG 02 — so the outermost piece comes first.',
  'shoot.notes': 'Notes for the stylist',
  'shoot.where': 'Where',
  'shoot.ownBackground': 'Your own background',
  'shoot.light': 'Light',
  'shoot.colour': 'Colour',
  'shoot.filmGrain': 'Film & grain',
  'shoot.grain': 'Grain',
  'shoot.vignette': 'Vignette',
  'shoot.aperture': 'Depth of field',
  'shoot.apertureHint':
    'Told to the renderer: only it knows what is near her and what is far. Film, grain and vignette are set afterwards, on each photograph.',
  'common.auto': 'Auto',
  'shoot.noGrain': 'No grain',
  'shoot.grainValue': 'GRAIN {n}',
  'shoot.takesCount': 'How many takes',
  'shoot.grainNote':
    'Grain is added after the render, not asked of it — so it is the same on every frame, and any frame can be redeveloped later without paying to shoot it again.',
  'shoot.shape': 'Shape',
  'shoot.loadingStudio': 'Loading the studio…',
  'shoot.recipes': 'House style',
  'shoot.recipeSave': 'Save these settings',
  'shoot.recipeName': 'Name this set-up',
  'shoot.recipeDefault': 'House style',
  'shoot.recipeSaved': 'Saved “{name}”. The garments are not part of it.',
  'shoot.recipeApplied': '“{name}” applied — add the clothes.',
  'shoot.recipeAppliedPartial': '“{name}” applied, minus a model that no longer exists.',
  'shoot.recipeForget': 'Forget {name}',
  'shoot.noModels': 'No one is on the books yet.',
  'shoot.notesPlaceholder': 'e.g. an off-white tank top under the vest, gold earrings, front hem tucked in',
  'shoot.copySpace': 'Room for type',
  'shoot.copy.none': 'None — fill the frame',
  'shoot.copy.left': 'Open space on the left',
  'shoot.copy.right': 'Open space on the right',
  'shoot.copy.top': 'Open space at the top',
  'shoot.copyHint':
    'For banners and campaign pages: the subject sits to one side and the other third stays clear, so a headline has somewhere to go.',
  'shoot.styleNotes': 'Style notes',
  'shoot.styleNotesPlaceholder': 'clean e-commerce, no stylization',
  'shoot.untitledRoll': 'Shoot',
  'shoot.name': 'Shoot name',
  'shoot.go': 'Shoot',
  'shoot.going': 'Rolling…',
  'shoot.blocked.limit': 'Over the {n}-frame cap set on this server — trim it back.',
  'shoot.blocked.model': 'Cast a model to continue.',
  'shoot.blocked.garments': 'Add at least one clothing photo.',
  'shoot.blocked.povs': 'Choose at least one camera set-up.',
  'shoot.failed': 'Could not start the shoot.',
  'shoot.rolling': 'Rolling — {n} frames on “{name}”',
  'shoot.rollingOne': 'Rolling — 1 frame on “{name}”',

  'pose.search': 'Search positions — reclining, walking, hands…',
  'pose.searchAria': 'Search positions',
  'pose.needsSupport': 'needs support',
  'posture.standing': 'standing',
  'posture.walking': 'walking',
  'posture.seated': 'seated',
  'posture.reclining': 'reclining',
  'posture.crouching': 'crouching',
  'posture.leaning': 'leaning',
  'scene.bringsLight': 'brings its light',
  'scene.studio': 'studio',
  'scene.custom': 'Custom',
  'scene.describe': 'Describe your background',
  'scene.removePhoto': 'Remove background photo',
  'wardrobe.what': 'What is it? e.g. sequinned vest',
  'wardrobe.notes': 'Anything the photo gets wrong (optional)',
  'wardrobe.notesShort': 'Note (optional)',
  'wardrobe.kind': 'Kind',
  'wardrobe.layer': 'Layer',
  'wardrobe.detailHintShort': 'Fine details: add one or more close-ups (zip, sleeve, print…).',

  'shoot.castOf': '{done}/{total} cast',
  'shoot.portraitOnFile': 'portrait on file',
  'shoot.noPortrait': 'no portrait',
  'shoot.noPortraitWarning':
    '{names} has no portrait. Without one the face drifts between shots — generate one on the Talent page.',
  'wardrobe.add': 'Add clothing photos',
  'wardrobe.photograph': 'Photograph',
  'capture.heading': 'Worth another shot: {n} of these photographs may cost you renders.',
  'capture.soft': 'looks out of focus — hold still, tap to focus on the garment',
  'capture.dark': 'very dark — move to a window or turn a light on',
  'capture.bright': 'blown out — turn the flash off, or move out of direct sun',
  'capture.small': 'quite small — get closer rather than cropping later',
  'capture.dismiss': 'Got it',
  'wardrobe.uploading': 'Uploading…',
  'voice.dictate': 'Dictate',
  'voice.stop': 'Stop',

  'filter.all': 'All',
  'filter.none': 'None',
  'group.full-body': 'Full body',
  'group.portrait': 'Portrait & crops',
  'group.detail': 'Details',
  'group.dynamic': 'Movement',
  'group.angle': 'Camera angles',
  'group.static': 'Standing',
  'group.movement': 'Movement',
  'group.ground': 'Ground & seated',
  'group.prop': 'Against something',
  'group.gesture': 'Gesture & gaze',
  'scene.onLocation': 'On location',
  'scene.studioSweep': 'Studio sweep',
  'scene.fromPhoto': 'From your photograph',
  'scene.describeOrUpload': 'Describe it, or upload a photo',
  'scene.uploadPhoto': 'Upload a background photo',
  'scene.replacePhoto': 'Replace photo',
  'scene.uploadFailed': 'Could not upload that image.',
  'shoot.selectedOf': '{done}/{total} selected',
  'wardrobe.remove': 'Remove {label}',
  'wardrobe.fromWardrobe': 'From the wardrobe',
  'wardrobe.hideWardrobe': 'Hide the wardrobe',
  'wardrobe.loading': 'Opening the wardrobe…',
  'wardrobe.empty': 'Nothing on file yet — upload a piece and it stays here.',
  'wardrobe.analysed': 'read',
  'wardrobe.takeOff': 'Take off',
  'wardrobe.deleteForever': 'Delete {label} from the wardrobe',
  'wardrobe.confirmDelete':
    'Delete {label} from the wardrobe for good? Rolls that used it keep their frames.',
  'wardrobe.saveFailed': 'Could not save that change.',
  'wardrobe.view.front': 'front',
  'wardrobe.view.back': 'back',
  'wardrobe.view.side': 'side',
  'wardrobe.view.detail': 'detail',
  'wardrobe.view.label': 'care label',
  'wardrobe.exampleTag': 'Example',
  'wardrobe.example.front':
    'One piece per photo, from the front, flat or on a hanger, on a plain background.',
  'wardrobe.example.back': 'The back, flat or on a hanger, like the front.',
  'wardrobe.example.side': 'From the side, for the cut and the side seam.',
  'wardrobe.example.detail': 'A close-up: zip, seam, print, embroidery…',
  'wardrobe.example.label': 'The care label, sharp and readable.',
  'wardrobe.removeView': 'Remove the {kind} photograph',
  'wardrobe.details': 'Details',
  'wardrobe.detailWhere': 'Where? (zip, sleeve…)',
  'wardrobe.detailWhereN': 'Where detail {n} is',
  'wardrobe.layer1': 'next to skin',
  'wardrobe.layer5': 'mid layer',
  'wardrobe.layer10': 'outer layer',
  'wardrobe.layer12': 'over everything',
  'dress.look': 'The look',
  'dress.addPiece': 'Add a piece',
  'dress.newPiece': 'New piece',
  'dress.newKind': 'New piece: {kind}',
  'dress.changeKind': 'Change the kind',
  'dress.the.kind': 'what kind?',
  'dress.hint.kind': 'Choose the kind first: the example to follow and the layer fit it.',
  'dress.viewOf': '{n} of {of} views',
  'dress.viewsOf': '{n} of {of} views',
  'dress.complete': 'Complete',
  'dress.views': 'Sides of the piece',
  'dress.removePiece': 'Remove this piece',
  'dress.added': 'Added',
  'dress.skip': 'Skip',
  'dress.done': 'Piece done',
  'dress.addDetail': 'Add a close-up',
  'dress.closet': 'Your wardrobe',
  'dress.closetDone': 'Done',
  'dress.dropOrPick': 'Drop it here, or press to choose',
  'dress.dropOrPickMany': 'Drop one or more here, or press to choose',
  'dress.view.front': 'Front',
  'dress.view.back': 'Back',
  'dress.view.side': 'Side',
  'dress.view.detail': 'Details',
  'dress.view.label': 'Label',
  'dress.view.ghost': 'Ghost',
  'dress.the.ghost': 'the ghost photos',
  'dress.side.left': 'Left profile',
  'dress.side.right': 'Right profile',
  'dress.yours.side.left': 'Your photo of the left profile',
  'dress.yours.side.right': 'Your photo of the right profile',
  'dress.hint.ghost':
    'Shoots start from these photos: the piece alone, no mannequin, hanger or other garment.',
  'dress.ghostRedo': 'Redo them all · {cost}',
  'dress.ghostRetouch': 'Redo',
  'dress.ghostWhat': 'e.g. the embroidery on the left sleeve is too small (optional)',
  'dress.ghostWhatLabel': 'Redo the “{side}” ghost photo — what to fix?',
  'dress.ghostRetouchGo': 'Redo · {cost}',
  'dress.ghostMaking': 'Being made…',
  'dress.ghostWaiting': 'Not made yet',
  'dress.ghostFailed': 'The ghost photos could not be made.',
  'dress.the.front': 'the front',
  'dress.the.back': 'the back',
  'dress.the.side': 'the side',
  'dress.the.detail': 'the details',
  'dress.the.label': 'the label',
  'dress.yours.new': 'Your photo of the front',
  'dress.yours.back': 'Your photo of the back',
  'dress.yours.side': 'Your photo of the side',
  'dress.yours.detail': 'Your close-ups',
  'dress.yours.label': 'Your photo of the label',
  'dress.hint.new': 'Only the front is needed; the back, side and close-ups make the piece truer.',
  'dress.hint.front': 'Its name, kind and layer tell the studio how to dress the model.',
  'dress.hint.back': 'Without the back, the studio imagines it from the front.',
  'dress.hint.side':
    'One profile is enough; both are better. Without one, the cut is read from the front and the back.',
  'dress.hint.detail': 'Close-ups keep prints, zips and embroidery faithful. Up to 6.',
  'dress.hint.label': 'Read once for the fabric; it never appears in the photos.',
  'voice.listening': 'Listening — speak naturally, then press Stop.',

  'picker.available': '{n} available',
  'picker.chosen': '{n} chosen · shot in this order',
  'picker.noMatch': 'No position matches “{query}”.',
  'pov.crop': 'crop',
  'pov.full': 'full',

  /* ------------------------------ talent ------------------------------- */
  'talent.eyebrow': 'Casting',
  'talent.title': 'Talent',
  'talent.lede':
    'A model is a face you reuse across shoots. Its portrait is attached to every render, which is what keeps the same person in every frame.',
  'talent.loadFailed': 'The talent list could not be loaded.',
  'talent.new': 'New model',
  'talent.display': 'Casting',
  'talent.newCard': 'New face',
  'talent.newCardHint': 'Describe them; the studio makes the front, the profile and the back.',
  'talent.find': 'Find a face',
  'talent.noneMatch': 'No face here answers to that.',
  'talent.only.all': 'Everyone',
  'talent.only.ready': 'Has a face',
  'talent.only.blank': 'No face yet',
  'talent.consistency': 'Mean identity score across finished frames',
  'talent.empty': 'No one on the books.',
  'talent.emptyHint':
    'Describe someone in a sentence and the studio will design and photograph them, or fill in the details yourself.',
  'talent.emptyCta': 'Cast your first model',
  'talent.source.manual': 'Studio · in house',
  'talent.source.designed': 'Studio · designed',
  'talent.source.uploaded': 'Studio · from reference',
  'talent.pending': '{n} takes to review',
  'talent.pendingOne': '1 take to review',
  'talent.health.bad': 'No face on file',
  'talent.health.badHint': 'Every render invents one.',
  'talent.health.warn': 'One angle',
  'talent.health.warnHint': 'Profiles and turns may drift.',
  'talent.health.ok': 'Locked',
  'talent.health.okHint': '{n} angles on file.',
  'talent.edit': 'Edit {name}',

  /* --------------------------- model editor ---------------------------- */
  'caster.new': 'Cast a model',
  'caster.newSub': 'A face you reuse across shoots. The identity line anchors every frame.',
  'caster.editSub': 'Shoot takes until the face is right, then keep the one you want.',
  'caster.noPortrait': 'No portrait yet',
  'caster.shooting': 'Shooting…',
  'caster.shootTakes': 'Shoot {n} takes',
  'caster.shootTake': 'Shoot 1 take',
  'caster.shootPortrait': 'Shoot a portrait',
  'caster.anotherAngle': 'Another angle of her',
  'caster.thin':
    'Describe the model first — a brief above, or hair and ethnicity below. From an empty form the portrait is just “a woman”, and you get a different stranger every press.',
  'caster.fromFile':
    'Shot from the portraits already on file, so it is the same person from another angle — not a new stranger who matches the words.',
  'caster.fromLine': 'Shot from the identity line, on a plain grey studio backdrop.',
  'caster.onFile': 'On file',
  'caster.primaryHint': 'The first is the face shown everywhere; press another to promote it.',
  'caster.makePrimary': 'Make this the primary portrait',
  'caster.isPrimary': 'Primary portrait',
  'caster.removePortrait': 'Remove this portrait',
  'caster.upload': 'Upload your own',
  'caster.turnaround': '360° view',
  'caster.turnaroundHint':
    'Three full-length photographs — front, profile, back — to keep her figure, proportions and hair from every angle. Each frame is sent the one from its own side.',
  'caster.view.front': 'Front',
  'caster.view.profile': 'Profile',
  'caster.view.back': 'Back',
  'caster.view.face': 'Face',
  'caster.shoot360': 'Shoot the 360° portrait',
  'caster.redo360': 'Shoot the 360° again',
  'caster.make360': 'Shoot the 360°',
  'caster.step.portrait': 'The face…',
  'caster.step.views': 'Front, profile, back…',
  'caster.describe': 'Description',
  'caster.newTakes': 'New takes of the face',
  'caster.redrawSide': 'Draw {side} again',
  'caster.thinShort': 'Describe them first: a brief, or the hair and the origin.',
  'caster.newTitle': 'New model',
  'caster.go': 'Create',
  'caster.removeView': 'Remove the {side} view',
  'caster.consentFirst': 'Tick the permission box above first.',
  'caster.manualHint':
    'Add a photo of her face, then generate her 360° in a beige bodysuit — or add the front, profile and back yourself. Nothing is read from her photo or written for her.',
  'caster.addPhoto': 'Add',
  'caster.make360Beige': 'Generate her 360° in a beige bodysuit',
  'caster.redo360Beige': 'Generate her 360° again',
  'caster.addSlot': 'Add the photo: {side}',
  'caster.replaceSlot': 'Replace the photo: {side}',
  'caster.needFace': 'Add a photo of her face.',
  'caster.drawViews': 'Draw the missing views',
  'caster.drawingViews': 'Rendering the views…',
  'caster.drawViewsHint': 'Drawn from her portrait, in plain clothes on grey. Each view counts as one image.',
  'caster.drawViewsNeedsFace': 'Add a portrait first: the views are drawn from her face.',
  'caster.drawViewsNeedsSave': 'Upload her photographs here, or save the model to draw her views.',
  'caster.errViews': 'The views could not be drawn.',
  'caster.consent': 'I have the right to use this person’s likeness, and they are an adult.',
  'error.consent_required': 'Confirm you have the right to use this likeness before uploading.',
  'error.minor': 'This studio only renders adults, and that photograph does not clearly show one.',
  'error.explicit': 'That photograph cannot be used here.',
  'error.no_person': 'That photograph does not show a face.',
  'caster.reading': 'Reading the face…',
  'caster.readFromPhoto':
    'Description read from your photo, so the words and the face agree. Every shot matches this photograph.',
  'caster.takes': 'Takes · pick one',
  'caster.takesOnTable': '{n} on the table',
  'caster.takesHint':
    'Nothing here is attached to a shoot yet. Keep the one you want as her portrait; throw the rest away.',
  'caster.keep': 'Keep',
  'caster.discard': 'Discard',
  'caster.keepAria': 'Keep this take',
  'caster.discardAria': 'Discard this take',
  'caster.brief': 'Start from a brief',
  'caster.briefPlaceholder': 'Scandinavian woman, late 20s, short platinum crop',
  'caster.design': 'Design & photograph',
  'caster.designing': 'Designing…',
  'caster.designHint': 'Fills in everything below and shoots a portrait to match.',
  'caster.name': 'Name',
  'caster.unset': 'Not said',
  'caster.ethnicityPlaceholder': 'e.g. East Asian',
  'caster.featuresPlaceholder': 'e.g. freckles across the nose',
  /* ------------------------------ enumerations -------------------------- *
   * Closed sets the server stores as English identifiers and the interface
   * used to print raw: a French studio read "female", "slim" and "complete"
   * on screens where every other word had been translated. The identifier is
   * the contract; these are what it is called.
   * --------------------------------------------------------------------- */
  'enum.gender.female': 'Female',
  'enum.gender.male': 'Male',
  'enum.gender.non-binary': 'Non-binary',
  'enum.build.slim': 'Slim',
  'enum.build.athletic': 'Athletic',
  'enum.build.straight': 'Straight',
  'enum.build.curvy': 'Curvy',
  'enum.build.plus': 'Plus',
  'enum.build.muscular': 'Muscular',
  'enum.status.draft': 'Draft',
  'enum.status.queued': 'Queued',
  'enum.status.analyzing': 'Reading the clothes',
  'enum.status.styling': 'Styling',
  'enum.status.rendering': 'Rendering',
  'enum.status.reviewing': 'Reviewing',
  'enum.status.complete': 'Complete',
  'enum.status.failed': 'Failed',
  'enum.status.cancelled': 'Cancelled',
  'enum.category.top': 'Top',
  'enum.category.outerwear': 'Outerwear',
  'enum.category.bottom': 'Bottom',
  'enum.category.dress': 'Dress',
  'enum.category.shoes': 'Shoes',
  'enum.category.bag': 'Bag',
  'enum.category.accessory': 'Accessory',
  'enum.category.headwear': 'Headwear',

  'caster.groupWho': 'Who they are',
  'caster.groupColouring': 'Colouring',
  'caster.groupHair': 'Hair',
  'caster.groupDetails': 'Anything else',
  'caster.gender': 'Gender',
  'caster.build': 'Build',
  'caster.age': 'Age range',
  'caster.height': 'Height',
  'caster.ethnicity': 'Ethnicity',
  'caster.skinTone': 'Skin tone',
  'caster.hairColour': 'Hair colour',
  'caster.hairLength': 'Hair length',
  'caster.hairStyle': 'Hair style',
  'caster.eyeColour': 'Eye colour',
  'caster.makeup': 'Makeup',
  'caster.features': 'Distinguishing features',
  'caster.identity': 'Identity line',
  'caster.rebuild': 'Rebuild from the form',
  'caster.identityTouched': 'Edited by hand — the fields above no longer change it.',
  'caster.identityDerived': 'Built from the fields above. Edit it and it stops tracking them.',
  'caster.savingAs': 'Saving as “{name}”',
  'caster.unsaved': 'Unsaved changes',
  'caster.upToDate': 'Up to date',
  'caster.needName': 'Give the model a name to save.',
  'caster.saveNew': 'Save to the books',
  'caster.saveEdit': 'Save changes',
  'caster.saving': 'Saving…',
  'caster.saved': 'Saved “{name}”',
  'caster.added': '{name} is on the books',
  'caster.removed': '{name} is off the books',
  'caster.cast': 'Cast — that take is now her portrait',
  'caster.errNoDesign': 'Could not design a model.',
  'caster.errNoPortrait': 'Could not render a portrait.',
  'caster.errNoKeep': 'Could not keep that take.',
  'caster.errNoDiscard': 'Could not discard that take.',
  'caster.errNoReorder': 'Could not reorder.',
  'caster.errNoSave': 'Could not save.',
  'caster.errNoDelete': 'Could not delete.',
  'caster.errPartial': '{done} of {total} came back — {message}',
  'caster.errUpload': 'Upload failed.',

  /* ------------------------------- rolls ------------------------------- */
  'rolls.eyebrow': 'Archive',
  'rolls.title': 'Rolls',
  'rolls.lede': 'Every set you have shot.',
  'rolls.only.all': 'All',
  'rolls.only.running': 'Developing',
  'rolls.only.done': 'Finished',
  'rolls.count': '{n} rolls',
  'rolls.countOne': '1 roll',
  'rolls.column': 'Frames',
  'rolls.new': 'New shoot',
  'rolls.empty': 'Nothing shot yet.',
  'rolls.emptyCta': 'Set up your first shoot',
  'rolls.deleted': 'Deleted “{name}”',
  'rolls.delete': 'Delete {name}',
  'rolls.confirmDelete': 'Delete “{name}”?',
  'rolls.confirmDeleteBody':
    'All {n} frames on this roll go with it, and they were paid for. This cannot be undone.',
  'rolls.status.rolling': 'rolling',
  'rolls.display': 'Your shoots',
  'rolls.mode.copy': 'Recreate',
  'rolls.mode.presets': 'Compose',
  'rolls.mode.brief': 'Describe',
  'rolls.mode.scene': 'Scene',
  'rolls.mode.poses': 'Poses',
  'rolls.kept': '{n} kept',
  'rolls.keptOne': '1 kept',
  'rolls.open': 'Open {name}',

  /* ------------------------------ one roll ----------------------------- */
  'roll.cut': 'Cut',
  'roll.saveRecipe': 'Save as house style',
  'roll.working': 'Working…',
  'roll.failed': 'This shoot failed.',
  'roll.look': 'The look',
  'roll.continuity': 'Across the roll',
  'roll.drift.major': 'Out of step',
  'roll.drift.minor': 'Slight',
  'roll.driftFrame': 'Frame {n}',
  'loupe.backToCurrent': 'Back to the current one',
  'loupe.takeCurrentShort': 'Current',
  'loupe.downloadOriginal': 'Download',
  'loupe.downloadPrint': 'Download for print (2×)',
  'loupe.downloadHint':
    'For print: twice the size and sharpened, for a catalogue or a printed page. No detail is invented.',
  'loupe.toFix': 'What the judge found',
  'loupe.takesOne': '1 take',
  'loupe.takesN': '{n} takes',
  'roll.driftCount': '{n} frames out of step',
  'roll.driftCountOne': '1 frame out of step',
  'roll.log': 'Studio log',
  'roll.frames': 'Frames',
  'roll.setUps': 'Set-ups',
  'roll.format': 'Format',
  'roll.notLoaded': 'Could not load this shoot.',
  'roll.inspect': 'Inspect frame {n}',
  'roll.queuedFrame': 'Queued',
  'roll.checkingFrame': 'Quality check',
  'roll.ready': 'frames ready',
  'roll.dockDone': '{n} frames ready',
  'roll.developingFrame': 'Developing…',
  'roll.frameFailed': 'Failed',
  'roll.referenceBadge': 'Reference',
  'roll.referenceApprovedBadge': 'Reference approved',
  'roll.referenceTitle': 'Approve the reference photo of each place',
  'roll.referenceHint':
    'When the model and the clothes are right, approve it: the {n} other poses are drawn from it, without touching the model, the clothes or the place. If not, correct it (for example “the jacket is not faithful, rework it”) or reshoot it.',
  'roll.referenceHintOne':
    'When the model and the clothes are right, approve it: the other pose is drawn from it, without touching the model, the clothes or the place. If not, correct it (for example “the jacket is not faithful, rework it”) or reshoot it.',
  'roll.approveReference': 'Approve and draw the {n} poses',
  'roll.approveReferenceOne': 'Approve and draw the other pose',
  'roll.approving': 'Approving…',
  'roll.approveNotReady': 'The reference is not ready yet: wait for it, or keep one of its versions.',
  'roll.referenceApproved': 'Reference approved — the other poses are being drawn.',
  'roll.approveError': 'Could not approve this reference.',
  'roll.waitsReference': 'Waits for the reference',
  'roll.markError': 'Could not save that mark.',
  'roll.updated': 'Frame updated',

  /* ------------------------------- loupe ------------------------------- */
  'loupe.render': 'Render',
  'loupe.reference': 'Reference',
  'loupe.frameOnly': 'Frame only',
  'loupe.history': 'History',
  'loupe.historyCount': '{n} versions',
  'loupe.takeCurrent': 'On the sheet',
  'loupe.take.render': 'First take',
  'loupe.take.retry': 'Automatic retry',
  'loupe.take.repair': 'Continuity repair',
  'loupe.take.reshoot': 'Reshoot',
  'loupe.take.variation': 'Variation',
  'loupe.takeViewing': 'An earlier version — {origin}, {time}. The sheet still shows the current one.',
  'loupe.restoreTake': 'Use this version',
  'loupe.restoreFailed': 'Could not bring this version back.',
  'loupe.wipe': 'Wipe between render and reference',
  'loupe.exposure': 'Exposure',
  'loupe.exp.setUp': 'Set-up',
  'loupe.exp.frame': 'Frame',
  'loupe.exp.pose': 'Pose',
  'loupe.exp.takes': 'Takes',
  'loupe.exp.defaultPose': 'set-up default',
  'loupe.quality': 'Quality check',
  'loupe.garmentFidelity': 'Garment fidelity',
  'loupe.identity': 'Identity',
  'loupe.scene': 'The scene',
  'loupe.publishScene': 'Offer as a Studio scene',
  'loupe.sceneOffered': '“{name}” is offered to every client.',
  'loupe.sceneName': 'Scene name',
  'loupe.pose': 'The pose',
  'loupe.realism': 'Realism',
  'loupe.reshotTimes': 'Reshot {n}× to correct issues.',
  'loupe.sent': 'What Gemini was sent',
  'loupe.sentStaff': 'Staff',
  'loupe.sentLoading': 'Loading…',
  'loupe.sentEmpty': 'Nothing on file for this version.',
  'loupe.sentMessage': 'Message {n}',
  'loupe.sentRefused': 'Refused ({reason})',
  'loupe.sentAnswer': 'Answer',
  'loupe.sentDraft': 'draft',
  'loupe.sentGrey': 'sent in grey',
  'loupe.sentVeiled': 'head blurred',
  'loupe.sentUnveiled.none': 'head not blurred: none found',
  'loupe.sentUnveiled.offline': 'head not blurred: offline',
  'loupe.sentCalls': 'Calls',
  'loupe.develop': 'Develop',
  'mark.keep': 'Keep',
  'mark.reject': 'Reject',
  'loupe.verdict': 'Verdict',
  'loupe.kept': 'Kept',
  'loupe.cut': 'Rejected',
  'loupe.undecided': 'Not decided',
  'loupe.prompt': 'The prompt it was made from',
  'loupe.negativeOnFile': 'Negative on file',
  'loupe.undeveloped': 'Undeveloped',
  'loupe.developFrame': 'Develop this frame',
  'loupe.developing': 'Developing…',
  'loupe.developFree': 'Free — it works from the negative, not the renderer.',
  'loupe.developWait': 'Available once this frame has finished shooting.',
  'loupe.developError': 'Could not develop that frame.',
  'loupe.fix': 'Fix and reshoot',
  'loupe.fixWritten': 'The critic wrote this correction. Edit it, then shoot this one frame again.',
  'loupe.fixBlank': 'Say what to change, then shoot this one frame again. The rest of the roll is untouched.',
  'loupe.fixPlaceholder': 'render the sequinned patches as small rectangles in a regular grid',
  'loupe.fromReferences':
    'Written, a correction edits the version on show: only what you ask for changes. Left blank, the frame is drawn again from the references, with every correction below.',
  'loupe.retouch': 'Correct this version',
  'loupe.referenceHint':
    'This is the reference photo of this place. Approve it if the model and the clothes are right: the other poses in this place are drawn from it without touching them.',
  'loupe.referenceApproved': 'Reference approved: the other poses in this place are drawn from it.',
  'loupe.retouchThat': 'Correct the version on show',
  'loupe.fixesSoFar': 'Corrections so far',
  'loupe.fixDrop': 'Remove the correction: {text}',
  'loupe.fixAnother': 'Another correction',
  'loupe.reshoot': 'Reshoot frame {n}',
  'loupe.reshooting': 'Shooting…',
  'loupe.stillShooting': 'Still shooting…',
  'loupe.reshootFailed': 'The reshoot failed.',
  'loupe.vary': 'Variation',
  'loupe.varyHint':
    'A variation of this image: the same place, your model and your outfit, the camera a step away and the pose a little different. One image.',
  'loupe.varyFailed': 'The variation failed.',

  /* ------------------------------- sheet ------------------------------- */
  'sheet.eyebrow': 'Contact sheet',
  'insight.title': 'What you keep',
  'insight.summary':
    '{kept} of {judged} judged frames circled, out of {frames} shot · {downloads} downloaded.',
  'insight.noneJudged':
    'Nothing judged yet — mark frames on a roll and this fills in. {n} frames shot so far.',
  'insight.bySetUp': 'By camera set-up',
  'insight.byGrade': 'By grade',
  'insight.byLight': 'By light',
  'insight.byScene': 'By scene',
  'insight.frames': '{n} frames',
  'insight.downloads': '{n} taken',
  'sheet.title': 'Every frame',
  'sheet.display': 'Contact sheet',
  'sheet.tabSheet': 'Sheet',
  'sort.tab': 'Quick sort',
  'sort.sorted': 'frames sorted',
  'sort.kept': '{n} kept',
  'sort.cut': '{n} cut',
  'sort.keptOne': '1 kept',
  'sort.cutOne': '1 cut',
  'sort.keep': 'Keep',
  'sort.reject': 'Cut',
  'sort.next': 'Up next',
  'sort.done': 'All sorted.',
  'sort.toSheet': 'See the sheet',
  'sort.undo': 'Undo the last one',
  'sort.open': 'Enlarge this frame',
  'sheet.lede':
    'Press a frame to open it. Keep the ones you want with the tick, cross out the ones you do not.',
  'sheet.export': 'Export {n} keepers',
  'sheet.exportOne': 'Export 1 keeper',
  'sheet.pulling': 'Pulling {n} keepers…',
  'sheet.pullingOne': 'Pulling 1 keeper…',
  'sheet.view.all': 'All',
  'sheet.view.keep': 'Keepers',
  'sheet.view.unmarked': 'Unjudged',
  'sheet.viewLabel': 'Which frames to show',
  'sheet.exportSize': 'Export size',
  'sheet.size1x': 'As shot',
  'sheet.size2x': '2×',
  'sheet.size2xHint':
    'Resampled to twice the size and sharpened, for catalogue and print. No detail is invented.',
  'sheet.anySetUp': 'Any set-up',
  'sheet.count': '{shown} of {total}',
  'sheet.empty': 'The sheet is blank.',
  'sheet.emptyHint': 'Finished frames from every shoot collect here.',
  'sheet.noKeepers': 'Nothing circled yet.',
  'sheet.noUnjudged': 'Nothing left unjudged.',
  'sheet.filterHint': 'Change the filter above to see the rest of the sheet.',
  'sheet.keeperCount': '{n} keepers',
  'sheet.keeperCountOne': '1 keeper',
  'sheet.markKeep': 'Mark as keeper',
  'sheet.markKeepOff': 'Remove keeper mark',
  'sheet.markReject': 'Mark as reject',
  'sheet.markRejectOff': 'Remove reject mark',
  /* -------------------------------- account ----------------------------- */
  'auth.signIn': 'Sign in',
  'auth.artKicker': 'Everything in one place',
  'auth.artLine': 'Your faces, your wardrobe and every frame you have shot, ready the moment you are.',
  'auth.signInLede': 'Your models, rolls and contact sheets are waiting.',
  'auth.createAccount': 'Open a studio',
  'auth.createLede': 'A place to keep your talent, your rolls and your sheets.',
  'auth.forgotTitle': 'Forgotten password',
  'auth.resetTitle': 'Choose a new password',
  'auth.name': 'Name',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.newPassword': 'New password',
  'auth.passwordHint': 'At least 10 characters. Longer beats complicated.',
  'auth.working': 'One moment…',
  'auth.sendLink': 'Send the link',
  'auth.setPassword': 'Set the password',
  'auth.forgot': 'Forgotten password?',
  'auth.needAccount': 'Open a studio',
  'auth.haveAccount': 'Back to sign in',
  'auth.checkMail': 'Check your mail',
  'auth.checkMailHint': 'If {email} is registered, a link is on its way. It works for an hour.',
  'auth.backToSignIn': 'Back to sign in',
  'auth.failed': 'That did not work. Try again.',
  'auth.verifyTitle': 'Confirm your email',
  'auth.verifying': 'One moment…',
  'auth.verified': 'Your address is confirmed. The studio is open.',
  'auth.verifyFailed': 'That link has expired or already been used.',
  'auth.verifyBanner': 'Rendering waits for you to confirm {email}. The link is in your inbox.',
  'auth.verifyResend': 'Send it again',
  'auth.verifySent': 'A new link is on its way.',
  'auth.continue': 'Into the studio',
  'auth.invitedLede': 'You are invited to try the studio. Choose a password to begin.',
  'auth.inviteInvalid': 'This invitation has expired, been used or been withdrawn. Ask for a new one.',
  'auth.inviteOnly': 'The studio is in private beta: accounts are by invitation.',
  'auth.listOnly': 'During the beta, only approved addresses can open a studio.',
  'auth.inviteFor': 'The invitation was sent to this address.',
  'auth.acceptBefore': 'I accept the',
  'auth.acceptAnd': 'and the',
  'auth.welcomeBack': 'Welcome back.',
  'auth.upTitle': 'Your studio starts here.',
  'auth.doors': 'Sign in or open a studio',
  'auth.word1': 'Recreate.',
  'auth.word2': 'Compose.',
  'auth.word3': 'Describe.',
  'legal.terms': 'terms of use',
  'legal.privacy': 'privacy notice',
  'error.invite_required': 'The studio is in private beta: accounts are by invitation.',
  'error.invite_invalid': 'This invitation is no longer valid, or it was sent to another address.',

  'settings.eyebrow': 'Account',
  'settings.noAccounts': 'This studio runs without accounts, so there is nothing to settle here.',
  'settings.title': 'Settings',
  'settings.signOut': 'Sign out',
  'settings.tab.profile': 'Profile',
  'settings.tab.usage': 'Usage',
  'settings.tab.security': 'Security',
  'settings.tab.appearance': 'Appearance',
  'settings.theme': 'Theme',
  'settings.nameHint': 'How everyone on the account sees you.',
  'settings.studioHint': 'Shared by everyone on the account.',
  'settings.themeHint': 'System follows your device.',
  'settings.languageHint': 'Kept with your account.',
  'settings.signOutHint': 'On this device.',
  'usage.thisMonth': 'This month',
  'usage.frames': 'Frames made',
  'usage.noLimit':
    'No plan and no cap: make as much as you like. Each frame is billed by the provider to your own API key.',
  'settings.you': 'You',
  'settings.emailFixed': 'Your address is how you sign in. Write in if it has to change.',
  'settings.studio': 'Studio',
  'settings.studioName': 'Studio name',
  'settings.members': 'People on the account',
  'settings.saved': 'Saved.',
  'settings.saveFailed': 'That could not be saved.',
  'settings.password': 'Password',
  'settings.currentPassword': 'Current password',
  'settings.changePassword': 'Change password',
  'settings.passwordChanged': 'Password changed. Other devices were signed out.',
  'settings.passwordEvicts': 'Changing it signs out every other device.',
  'settings.devices': 'Signed in on',
  'settings.revoke': 'Sign out',
  'settings.thisDevice': 'This device',
  'settings.deviceOn': '{browser} on {os}',
  'settings.deviceUnknown': 'Unknown device',
  'settings.lastSeen': 'Last used {when}',
  'settings.devicesMore': 'Show {n} more',
  'settings.devicesFewer': 'Show fewer',
  'settings.activity': 'Recent account activity',
  'settings.dangerZone': 'Close the studio',
  'settings.deleteWarning':
    'This deletes {name} and everything in it — models, rolls, frames and files. It cannot be undone.',
  'settings.typeName': 'Type “{name}” to confirm',
  'settings.deleteAccount': 'Delete this account',

  'calib.title': 'The judge, measured',
  'calib.notEnough': 'Mark frames on the contact sheet and this fills in — {n} of {min} so far.',
  'calib.agreement': 'The critic and you agree on {pct} of the {n} frames you have marked.',
  'calib.falseRejects': 'Re-rendered, you kept',
  'calib.falseAccepts': 'Delivered, you crossed',
  'calib.keptMean': 'Mean score, kept',
  'calib.rejectedMean': 'Mean score, crossed',
  'calib.suggested': 'A threshold of {n} would have matched your marks best — {pct} agreement.',

  /* Server refusals worth saying properly. Keyed by the code the server sends
     alongside its English sentence; anything without a key falls through to
     that sentence. */
  'error.account_suspended': 'This account is suspended. Get in touch and we will sort it out.',
  'error.email_unverified': 'Confirm your email address before rendering. The link is in your inbox.',
  'error.missing_reference':
    'A face or a piece on this roll is no longer on the books. Cast or add it again.',
  'error.veil_failed':
    'The head of the scene’s model could not be found to blur it: nothing was sent or charged. Try again in a moment.',
  'error.too_many_running': 'This account already has {max} rolls rendering. Let one finish first.',
  'error.spend_ceiling':
    'Rendering is paused for the rest of the day: the studio has reached its daily spending limit.',
  'error.verify_invalid': 'That link has expired or already been used.',
  'error.mail_unavailable': 'This server cannot send mail, so there is no link to send.',

  /* ------------------------------ front desk ---------------------------- */
  'admin.eyebrow': 'Front desk',
  'admin.title': 'The house',
  'admin.lede': 'Who is here, and what the studio has shot.',
  'admin.tab.overview': 'Overview',
  'admin.tab.catalogue': 'Catalogue',
  'cat.shelf.scenes': 'Scenes',
  'cat.addScene': 'Add a scene',
  'cat.addSceneHint': 'A finished photo: place, pose, outfit, your model',
  'cat.scenesHint':
    'Each scene is a finished photo. Clients put their model and their clothes in place of yours; everything else is kept. The thumbnails show the photo as Gemini is sent it: the model’s head blurred.',
  'cat.emptyScenes': 'No scenes yet. Add a photo, or offer a frame from one of your shoots.',
  'cat.removeScene': 'Remove “{name}”?',
  'cat.sceneName': 'Scene name',
  'cat.lede':
    'What every client is offered in “New shoot”: models, places, poses and scenes. Clients can use them; only you can change them.',
  'cat.shelf.models': 'Models',
  'cat.shelf.places': 'Places',
  'cat.shelf.poses': 'Poses',
  'cat.addModel': 'New model',
  'cat.edit': 'Edit',
  'cat.saved': 'Saved to the catalogue.',
  'cat.failed': 'That could not be done.',
  'cat.removeBody': 'It disappears from every client’s choices. Shoots already made keep it.',
  'cat.emptyModels': 'No models in the catalogue yet: create the first one.',
  'cat.emptyPlaces':
    'No places of your own in the catalogue yet. The built-in sets below are always offered.',
  'cat.placesHint':
    'Click a place to change its name, description or photo, or to delete it. The built-in sets are offered as well.',
  'cat.builtinPlaces': 'Built-in sets',
  'cat.builtin': 'Built in',
  'cat.builtinHint':
    'Offered in every roll, beside the catalogue’s own places. They are part of the studio and cannot be changed here.',
  'cat.emptyPoses': 'No poses yet: add photos of people in the poses you want to offer, one pose per photo.',
  'cat.posesHint':
    'Click a pose to rename it. Nothing is read or generated: the photo is reproduced as it is.',
  'admin.tab.accounts': 'Studios',
  'admin.tab.invites': 'Invitations',
  'admin.invite.new': 'New invitation',
  'admin.invite.hint':
    'With an address, the link works for that address only and is emailed when mail is set up. Without one, it works for the first person who opens it. Valid for 14 days, once.',
  'admin.invite.email': 'Email address (optional)',
  'admin.invite.emailPlaceholder': 'name@example.com',
  'admin.invite.note': 'Note (optional)',
  'admin.invite.notePlaceholder': 'Who it is for, and why',
  'admin.invite.create': 'Create invitation',
  'admin.invite.emailed': 'Invitation sent to {email}. The link is below as well.',
  'admin.invite.copyNow': 'Copy this link now: it will not be shown again.',
  'admin.invite.link': 'Invitation link',
  'admin.invite.copy': 'Copy',
  'admin.invite.copied': 'Link copied.',
  'admin.invite.count': 'Invitations: {n}',
  'admin.invite.open': 'Link without an address',
  'admin.invite.usedBy': 'Used by {email} on {date}',
  'admin.invite.expires': 'Expires {date}',
  'admin.invite.revoke': 'Withdraw',
  'admin.invite.status.pending': 'Pending',
  'admin.invite.status.used': 'Used',
  'admin.invite.status.expired': 'Expired',
  'admin.invite.status.revoked': 'Withdrawn',
  'admin.failed': 'That did not work.',
  'admin.done': 'Done.',

  'admin.stat.accounts': 'Studios',
  'admin.stat.new30': '{n} opened in 30 days',
  'admin.stat.people': 'People',
  'admin.stat.active30': '{n} signed in this month',
  'admin.stat.framesPeriod': 'Frames this period',
  'admin.stat.spend': 'Spend this period',
  'admin.stat.spendFoot': 'Provider calls, across {n} frames',
  'admin.stat.perFrame': 'Cost per frame',
  'admin.stat.perFrameFoot': 'Renders, critiques and prompting, all in',
  'admin.stat.spendTop': 'Biggest line',
  'admin.detail.spend': 'Spend this period',
  'roll.cost': 'Cost',
  'roll.costHint': '{render} to render, {critique} to check, {setUp} to set up',
  'admin.stat.rolls': 'Rolls',
  'admin.stat.models': '{n} models',
  'admin.stat.frames': 'Frames, all time',
  'admin.stat.failed': '{n} failed',
  'admin.stat.storage': 'Storage',
  'admin.stat.storageFoot': 'Uploads and renders',

  'admin.chart.frames': 'Frames a day, last fortnight',
  'admin.chart.total': '{n} in 14 days',
  'admin.chart.empty': 'No frames finished in the last fortnight.',
  'admin.chart.aria': '{n} frames finished across the last {days} days.',

  'admin.health': 'Worth knowing',
  'admin.health.suspended': 'Suspended',
  'admin.health.staff': 'Front desk',
  'admin.health.staffHint':
    'Staff comes from ADMIN_EMAILS on the server. Nothing in this app can grant it, which is the point.',

  'admin.searchPlaceholder': 'Name, account id or email',
  'admin.accountsFound': '{n} studios',
  'admin.suspended': 'suspended',
  'admin.framesShort': '{n} frames',
  'admin.detail.frames': 'Frames shot',
  'admin.detail.storage': 'Storage',

  'admin.danger': 'Heavy handed',
  'admin.suspend': 'Suspend this studio',
  'admin.restore': 'Lift the suspension',
  'admin.deleteAccount': 'Delete this studio',
} as const;
